export const SITE_SETTINGS_KEY = 'joleu-site-settings';
export const LEGACY_BLOG_THEME_KEY = 'blog-theme';
export const SITE_SETTINGS_APPLY_EVENT = 'joleu:site-settings-apply';

export type ThemePreference = 'system' | 'light' | 'dark';

export interface SiteSettings {
  theme: ThemePreference;
  /** User request: always use opaque / non-blurred glass (in addition to OS reduced-transparency). */
  reducedTransparency: boolean;
  /** User request: minimize motion (in addition to OS prefers-reduced-motion). */
  reducedMotion: boolean;
}

const defaults: SiteSettings = {
  theme: 'system',
  reducedTransparency: false,
  reducedMotion: false,
};

export function getSystemColorScheme(): 'light' | 'dark' {
  if (typeof window === 'undefined' || !window.matchMedia) return 'dark';
  return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
}

/** Calls `onChange` when the OS switches between light and dark (useSyncExternalStore subscriber). */
export function subscribeSystemColorScheme(onChange: () => void): () => void {
  if (typeof window === 'undefined' || !window.matchMedia) return () => {};
  const mq = window.matchMedia('(prefers-color-scheme: light)');
  mq.addEventListener('change', onChange);
  return () => mq.removeEventListener('change', onChange);
}

export function resolveThemePreference(pref: ThemePreference): 'light' | 'dark' {
  if (pref === 'system') return getSystemColorScheme();
  return pref;
}

function parseStored(json: string | null): Partial<SiteSettings> | null {
  if (!json) return null;
  try {
    const v = JSON.parse(json) as unknown;
    if (!v || typeof v !== 'object') return null;
    return v as Partial<SiteSettings>;
  } catch {
    return null;
  }
}

export function loadSiteSettings(): SiteSettings {
  if (typeof localStorage === 'undefined') return { ...defaults };

  const raw = localStorage.getItem(SITE_SETTINGS_KEY);
  let parsed = parseStored(raw);

  if (!parsed) {
    const legacyBlog = localStorage.getItem(LEGACY_BLOG_THEME_KEY);
    if (legacyBlog === 'light' || legacyBlog === 'dark') {
      parsed = { theme: legacyBlog, reducedTransparency: false, reducedMotion: false };
      localStorage.removeItem(LEGACY_BLOG_THEME_KEY);
      localStorage.setItem(SITE_SETTINGS_KEY, JSON.stringify({ ...defaults, ...parsed }));
    }
  }

  return {
    ...defaults,
    ...parsed,
    theme: parsed?.theme === 'light' || parsed?.theme === 'dark' || parsed?.theme === 'system'
      ? parsed.theme
      : defaults.theme,
    reducedTransparency: Boolean(parsed?.reducedTransparency),
    reducedMotion: Boolean(parsed?.reducedMotion),
  };
}

export function saveSiteSettings(s: SiteSettings): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(SITE_SETTINGS_KEY, JSON.stringify(s));
}

const THEME_COLOR_DARK = '#0a0d14';
const THEME_COLOR_LIGHT = '#f2f3f9';

export function updateThemeColorMeta(resolved: 'light' | 'dark'): void {
  const meta = document.querySelector('meta[name="theme-color"]');
  if (!meta) return;
  meta.setAttribute('content', resolved === 'light' ? THEME_COLOR_LIGHT : THEME_COLOR_DARK);
}

export function applySiteSettingsToDocument(s: SiteSettings): void {
  if (typeof document === 'undefined') return;

  const resolved = resolveThemePreference(s.theme);
  document.documentElement.dataset.theme = resolved;

  if (s.reducedTransparency) document.documentElement.dataset.reducedTransparency = 'on';
  else delete document.documentElement.dataset.reducedTransparency;

  if (s.reducedMotion) document.documentElement.dataset.reducedMotion = 'on';
  else delete document.documentElement.dataset.reducedMotion;

  updateThemeColorMeta(resolved);
  window.dispatchEvent(new Event(SITE_SETTINGS_APPLY_EVENT));
}

export function effectiveReducedMotion(): boolean {
  if (typeof document === 'undefined') return false;
  return (
    document.documentElement.dataset.reducedMotion === 'on' ||
    (typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true)
  );
}

export function effectiveReducedTransparency(): boolean {
  if (typeof document === 'undefined') return false;
  return (
    document.documentElement.dataset.reducedTransparency === 'on' ||
    (typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-transparency: reduce)').matches === true)
  );
}
